import React, { useEffect, useRef, useState } from 'react';
import { parseSpreadsheet, slugify } from './csv';
import {
  fetchCoursesOnce, listenImportBatches, logImportBatch,
  upsertAssignments, upsertCourses, fetchSchoolCalendar,
} from './api';
import { isoWeekdayNumber, weekNumberForDate } from './calendarMath';
import { BLOCK_IDS as VALID_BLOCKS } from './schema';

function parseRow(raw, rowNum) {
  const errors = [];
  const date = raw.date ?? '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) errors.push('date must be YYYY-MM-DD');

  const blockRaw = (raw.block ?? '').toLowerCase();
  const block = VALID_BLOCKS.includes(blockRaw) ? blockRaw : null;
  if (!block) errors.push(`block must be one of ${VALID_BLOCKS.join(', ')}`);

  const title = raw.title ?? '';
  if (!title) errors.push('title is required');

  const courseName = raw.course ?? '';
  if (!courseName) errors.push('course is required');

  const minutes = Number(raw.minutes);
  const estMinutes = Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : 20;

  const floorRaw = (raw.floor ?? '').toLowerCase();
  const isFloor = ['yes', 'true', '1'].includes(floorRaw);

  // Optional "review" column — defaults to true (needs a parent look) unless
  // explicitly set to no/false/0, e.g. for lean reading-choice rows.
  const reviewRaw = (raw.review ?? '').toLowerCase();
  const needsReview = !['no', 'false', '0'].includes(reviewRaw);

  return {
    rowNum, raw, errors, date, block,
    courseId: slugify(courseName || 'item'),
    courseName: courseName || 'Item',
    title,
    subLabel: raw.details || undefined,
    resourceLink: raw.link || undefined,
    estMinutes,
    isFloor,
    needsReview,
  };
}

function slugCourseTitle(r) {
  return slugify(`${r.courseId}-${r.title}`).slice(0, 40);
}

// Bulk one-off assignments (field trips, co-op electives, extra practice)
// via CSV/XLSX — ported from school-site's ImportPage. Owner-only, keyed to
// the currently selected homeschool student (see OwnerSchoolModule).
export default function ImportTab({ familyData, student, currentUserEmail }) {
  const [rows, setRows] = useState(null);
  const [filename, setFilename] = useState('');
  const [existingCourses, setExistingCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInput = useRef(null);

  useEffect(() => {
    if (!familyData?.id) return;
    return listenImportBatches(familyData.id, setBatches);
  }, [familyData?.id]);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file || !familyData?.id) return;
    setResult(null);
    setFilename(file.name);
    const [parsed, courses] = await Promise.all([parseSpreadsheet(file), fetchCoursesOnce(familyData.id)]);
    setExistingCourses(courses);
    setRows(parsed.map((r, i) => parseRow(r, i + 2))); // +2: header row + 1-indexing
    if (fileInput.current) fileInput.current.value = '';
  }

  async function commit() {
    if (!rows || !familyData?.id || !student?.studentEmail) return;
    const valid = rows.filter(r => r.errors.length === 0);
    if (valid.length === 0) return;

    setCommitting(true);
    try {
      const calendar = await fetchSchoolCalendar(familyData.id);
      const stamp = Date.now();
      const now = new Date().toISOString();
      const knownCourseIds = new Set(existingCourses.map(c => c.id));
      const newCourses = [];

      const assignments = valid.map((r, i) => {
        if (!knownCourseIds.has(r.courseId)) {
          knownCourseIds.add(r.courseId);
          newCourses.push({ id: r.courseId, name: r.courseName, subjectArea: 'Imported', colorToken: '--c-afternoon' });
        }
        const weekNumber = calendar?.weeks ? weekNumberForDate(calendar.weeks, r.date) : 0;
        return {
          id: `csv__${r.date}__${slugCourseTitle(r)}-${stamp}-${i}`,
          studentEmail: student.studentEmail,
          courseId: r.courseId,
          date: r.date,
          weekNumber,
          dayOfWeek: isoWeekdayNumber(r.date),
          block: r.block,
          order: 500 + i,
          title: r.title,
          subLabel: r.subLabel,
          resourceLink: r.resourceLink,
          estMinutes: r.estMinutes,
          isFloor: r.isFloor,
          needsReview: r.needsReview,
          status: 'not_started',
          confusionFlag: false,
          reviewedByParent: false,
          source: 'csv_import',
          createdAt: now,
          updatedAt: now,
        };
      });

      await upsertCourses(familyData.id, newCourses);
      await upsertAssignments(familyData.id, assignments);
      await logImportBatch(familyData.id, {
        id: `batch-${stamp}`,
        filename,
        importedBy: currentUserEmail || 'owner',
        rowCount: assignments.length,
        createdAt: now,
        rawRows: valid.map(r => r.raw),
      });

      setResult(`Imported ${assignments.length} row(s) from ${filename}.`);
      setRows(null);
    } finally {
      setCommitting(false);
    }
  }

  const validCount = rows?.filter(r => r.errors.length === 0).length ?? 0;
  const invalidCount = (rows?.length ?? 0) - validCount;

  return (
    <div>
      <p className="settings-note" style={{ marginTop: 12 }}>
        Upload a CSV or XLSX of one-off assignments — field trips, co-op electives, extra practice.
        Columns: date (YYYY-MM-DD), block ({VALID_BLOCKS.join('/')}), course, title, details, link, minutes, floor (yes/no), review (no to skip parent review).
      </p>
      <p>
        <a href="/school-import-template.csv" download>Download the column template</a>
      </p>
      <input ref={fileInput} type="file" accept=".csv,.xlsx,.xls" onChange={handleFile} />

      {result && (
        <p className="settings-note" style={{ marginTop: 8, color: 'var(--good)' }}>{result}</p>
      )}

      {rows && (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '12px 0', flexWrap: 'wrap' }}>
            <span>{rows.length} rows parsed</span>
            <span>·</span>
            <span>{validCount} valid</span>
            {invalidCount > 0 && <span style={{ color: 'var(--bad, #b00)' }}>· {invalidCount} need fixing</span>}
            <button className="btn btn--primary btn--small" disabled={validCount === 0 || committing} onClick={commit}>
              {committing ? 'Importing…' : `Import ${validCount} row(s)`}
            </button>
            <button className="btn btn--ghost btn--small" onClick={() => setRows(null)}>Cancel</button>
          </div>

          <div className="edit-list">
            {rows.map(r => (
              <div key={r.rowNum} className="edit-list-row">
                <div className="edit-list-row__info">
                  <span className="edit-list-row__id-badge">{r.rowNum}</span>
                  <div>
                    <div className="edit-list-row__name">{r.title || '(no title)'}</div>
                    <div className="edit-list-row__sub">
                      {[r.date, r.block ?? r.raw.block, r.courseName, `${r.estMinutes} min`].filter(Boolean).join(' · ')}
                    </div>
                    {r.errors.length > 0 && (
                      <div className="edit-list-row__sub" style={{ color: 'var(--bad, #b00)' }}>{r.errors.join('; ')}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="form-label" style={{ marginTop: 20 }}>Import history</div>
      {batches.length === 0 ? (
        <p className="settings-note">No imports yet.</p>
      ) : (
        <div className="edit-list">
          {batches.map(b => (
            <div key={b.id} className="edit-list-row">
              <div className="edit-list-row__info">
                <span className="edit-list-row__id-badge">📄</span>
                <div>
                  <div className="edit-list-row__name">{b.filename}</div>
                  <div className="edit-list-row__sub">{b.rowCount} rows · {new Date(b.createdAt).toLocaleString()}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
